/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { HttpParams } from '@angular/common/http';
import { ApiService } from '@api/services/api/api.service';
import {
  PositionDTO,
  IdentificationVerifyStatus,
  NoteDTO,
  ChannelDTO
} from '@api/models/';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { Position } from '@models/position';
import { forkJoin } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PositionService {
  private position$: BehaviorSubject<Position> = new BehaviorSubject<Position>(
    null
  );

  private channel$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  constructor(private apiService: ApiService) {}

  /**
   * carica i dati della position richiesta
   */
  loadPosition(positionId: string): Observable<boolean> {
    if (!positionId) {
      return throwError('wrn.position_loading_error');
    }

    return forkJoin([
      this.apiService.getPosition(positionId),
      this.apiService.getChannel(positionId)
    ]).pipe(
      map(res => {
        this.setPosition(res[0]);
        this.setChannel(res[1]);
      }),
      map(() => true)
    );
  }

  /**
   * ricarica la position
   */
  reloadPosition(): Observable<boolean> {
    return this.loadPosition(this.getPositionId());
  }

  /**
   * restituisce osservabile per dati position
   */
  getPositionWhenChange(): Observable<Position> {
    return this.position$
      .asObservable()
      .pipe(filter(x => x instanceof Position));
  }

  /**
   * restituisce i dati della position
   */
  getPosition(): Position {
    return this.position$.getValue();
  }

  getChannelWhenChange(): Observable<string> {
    return this.channel$.asObservable().pipe(filter(x => !!x));
  }

  getChannel(): string {
    return this.channel$.getValue();
  }

  isSpecialChannel(): boolean {
    const channel = this.getChannel();
    const specialChannels = ['AGE'];

    return (
      typeof channel === 'string' &&
      specialChannels.includes(channel.toUpperCase())
    );
  }

  /**
   * restituisce il position id
   */
  getPositionId(): string {
    const position = this.getPosition();
    return position && position.data.positionId;
  }

  /**
   * restituisce l'url per attivare il flusso di riconoscimento
   */
  getRecognitionUrl(method: string): Observable<string> {
    const position = this.getPosition();

    return this.apiService
      .postIdentificationStart(method, {
        businessId: position.data.positionId,
        ndg: position.data.ndg
      })
      .pipe(map(res => res.consumerUrl));
  }

  /**
   * controlla che la procedura di riconoscimento sia stata completata
   */
  verifyRecognition(method: string): Observable<IdentificationVerifyStatus> {
    const position = this.getPosition();

    const params = new HttpParams()
      .set('businessId', position.data.positionId)
      .set('ndg', position.data.ndg);

    return this.apiService
      .getIdentificationVerify(method, { params })
      .pipe(map(res => (res && res.status) || null));
  }

  /**
   * restituisce l'url per attivare il flusso di firma digitale
   */
  getDigitalSignatureUrl(): Observable<string> {
    const position = this.getPosition();

    return this.apiService
      .getDigitalSignatureRequestDsp(position.data.positionId)
      .pipe(map(res => (res && res.providerUrl) || null));
  }

  /**
   * ritira la pratica
   */
  withdrawPosition(reasonCode: string): Observable<any> {
    const positionId = this.getPositionId();

    return this.apiService.postPositionWithdraw(positionId, { reasonCode });
  }

  /**
   * ritorna la lista di note
   */
  getNotes(type: string): Observable<NoteDTO[]> {
    const positionId = this.getPositionId();

    return this.apiService.getNotes(positionId, type);
  }

  /**
   * imposta la position
   */
  private setPosition(dto: PositionDTO): void {
    this.position$.next(new Position(dto));
  }

  /**
   * imposta il canale
   */
  private setChannel(dto: ChannelDTO): void {
    this.channel$.next((dto && dto.channel) || null);
  }
}
